import React from 'react';
import { useLocation } from 'react-router-dom';
import { useMutation } from 'react-apollo';
import { notification } from 'antd';
import CREATE_FRICTIONLESS_PR from '../../../GraphQl/Mutations/CREATE_FRICTIONLESS_PR';

const TagsManagerUpdateController = ({
  data,
  orgs,
  currentOrgId,
  currentUser,
  carts,
  onSuccess = () => { },
  updateEventTag,
  platforms,
  updateImpressionTag,
  regenerateTag,
  regenerateImpressionsTag,
  ...props
}) => {
  const { eventsTarget } = data;
  const location = useLocation();
  const [createFrictionlessPRMutation] = useMutation(CREATE_FRICTIONLESS_PR);

  const advertisers = orgs.orgs.map((org) => {
    return { value: org.id, label: org.name };
  });
  const getCarts = (carts && carts.getCarts) || [];
  const cart = getCarts.find((cart) => cart.value === eventsTarget.cart) || {
    label: 'Unsupported Cart',
    value: 'thirdparty', //Mark unsupported carts as thirdparty
    eventsTracked: [],
  };
  const details = {
    id: eventsTarget.id,
    name: eventsTarget.name,
    appId: eventsTarget.eventTags[0].appId[0],
    cartLabel: cart.label,
    cart: cart.value,
    advertiser: eventsTarget.orgs[0].id,
    ecommerce: eventsTarget.transactions > 0 ? true : eventsTarget.isCommerce,
    signups: eventsTarget.signups > 0 ? true : eventsTarget.isSignUps,
    isPublisherDomain: eventsTarget.isPublisherDomain,
    publisherDomain: eventsTarget.domain,
    isAdvertiser: eventsTarget.isAdvertiser,
    impressionEnvironments: eventsTarget.impressionEnvironments,
    dsp: eventsTarget.dsp,
    type: eventsTarget.type,
    dspTracking: eventsTarget.dspTracking,
    thirdPartyTag: eventsTarget.thirdPartyTag,
    orgAdvertiserId: eventsTarget.orgAdvertiserId,
    s3_pv: eventsTarget.s3_pv,
    s3_tr: eventsTarget.s3_tr,
  };

  const onSubmit = async (values) => {
    console.log(values);
    const searchParams = new URLSearchParams(location.search);
    const source = searchParams.get('source');

    switch (source) {
      case 'impression':
        return updateImpressionTag({
          variables: {
            data: {
              advertiserName: values.name,
              environment: values.dsp,
              orgId: values.advertiser,
              id: eventsTarget.id,
              appId: values.appId,
            },
          },
        });
      case 'publisher':
        let type = [];

        if (details.isPublisherDomain) type.push('PUBLISHER');
        if (values.isMulti === 'yes') {
          type.push('PUBLISHER');
          type.push('MEASUREMENT');
        }

        return updateEventTag({
          variables: {
            data: {
              id: eventsTarget.id,
              name: values.name,
              orgId: values.advertiser,
              publisherDomain: values.domainProtocol + values.domainUrl + values.domainExtension,
              isPublisherDomain: details.isPublisherDomain,
              isEcommerce: eventsTarget.isCommerce,
              isSignUps: eventsTarget.isSignUps,
              cart: eventsTarget.cart,
              domain: values.domain,
              appId: values.appId,
              type,
            },
          },
        });
      default:
        try {
          console.log("Updating event tag:");
          const result = await updateEventTag({
            variables: {
              data: {
                id: eventsTarget.id,
                name: values.name,
                orgId: values.advertiser,
                isEcommerce: values.ecommerce,
                cart: values.platform,
                isSignUps: values.signups,
                domain: values.domain,
                appId: values.appId,
                dspTracking: values.dspTracking,
                thirdPartyTag: values.thirdPartyTag,
                orgAdvertiserId: values.orgAdvertiserId,
                s3_pv: values.s3_pv,
                s3_tr: values.s3_tr,
              },
            },
          });

          if (values.dspTracking && values.s3_pv && values.s3_tr && values.appId) {
            const selectedOrg = orgs.orgs.find((org) => org.id === values.advertiser);
            const orgDomain = selectedOrg && selectedOrg.domain;

            console.log("Org domain: ", orgDomain);

            if (orgDomain) {
              try {
                const prResult = await createFrictionlessPRMutation({
                  variables: {
                    data: {
                      domain: orgDomain,
                      appId: values.appId,
                      s3_pv: values.s3_pv,
                      s3_tr: values.s3_tr,
                      dspTracking: values.dspTracking,
                      isUpdate: true,
                    },
                  },
                });

                if (prResult?.data?.createFrictionlessPR?.prUrl) {
                  console.log(`Frictionless PR updated successfully: ${prResult.data.createFrictionlessPR.prUrl}`);
                }
              } catch (prError) {
                console.error('Error creating frictionless PR:', prError);
              }
            } else {
              console.warn('Org domain not found, skipping frictionless PR creation');
              notification.warning({
                message: 'Advertiser Domain Required',
                description: 'Advertiser Domain should be configured and added when s3_pv and s3_tr are provided',
                duration: 6,
              });
            }
          } else {
            console.log('Frictionless PR update conditions not met. Skipping PR update.');
          }

          return result;
        } catch (error) {
          console.error('Error updating event tag:', error);
          throw error;
        }
    }
  };

  const onRegenerateTag = async (values) => {
    const searchParams = new URLSearchParams(location.search);
    const source = searchParams.get('source');

    if (source === 'impression' || eventsTarget.isAdvertiser) {
      await regenerateImpressionsTag({
        variables: {
          id: eventsTarget.id,
          appId: values.appId,
          environment: values.dsp,
        },
      });
    } else if (source === 'publisher' || eventsTarget.isPublisherDomain) {
      await regenerateTag({
        variables: {
          id: eventsTarget.id,
          domain: values.domainProtocol + values.domainUrl + values.domainExtension,
          cart: eventsTarget.cart,
        },
      });
    } else {
      await regenerateTag({
        variables: {
          id: eventsTarget.id,
          domain: values.domain,
          cart: values.platform,
        },
      });
    }
  };

  let tagType = 'Measurement';
  if (eventsTarget.isPublisherDomain) tagType = 'Publisher';

  return React.cloneElement(props.children, {
    ...props.children.props,
    details,
    advertisers,
    currentOrgId,
    currentUser,
    carts: getCarts,
    orgs,
    onSubmit,
    onRegenerateTag,
    tagType,
    platforms: platforms.getDSP,
  });
};

export default TagsManagerUpdateController;
